import { CustomElement } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { KeyboardInput } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { CjsInputTextEditor } from "@cervval.js/client-html5-ui/src/editors/inputTextEditor/inputTextEditor";
import { AuthErrorCode } from "@cervval.js/common-auth/src/auth.errors";
import { ERROR_MESSAGES } from "@cervval.js/common-auth/src/auth.errors.i18n";
import { APP_SERVICE, AppService, ERROR_MESSAGE_SERVICE, ErrorMessageService, LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { ErrorWithCode } from "@cervval.js/common/src/errors/errors";
import { Environment } from "@cervval.js/common/src/services/app.service";
import { ensureLoaded } from "@cervval.js/common/src/tools/object.tools";
import { DigitCodeComponent } from "../digitCodeComponent/digitCodeComponent";
import { I18nLoginPage } from "./loginComponent.i18n";

ensureLoaded(DigitCodeComponent);

export type LoginCallback = (login: string, password: string) => Promise<void>;
export type LinkOptions = {
    text?: string,
    tooltip?: string,
    iconCss?: string[],
    css?: string[],
    callback: () => Promise<void>
};

export type LoginSettings = {
    nbrDigitPassword: number,
    infoConnectionDisplay?: boolean,
    login?: LoginCallback,
    /** Callback to init code form */
    sendCallback?: (login: string) => Promise<boolean>,
    noMailTitle?: boolean,
    noPasswordTitle?: boolean,
    infoConnectionMail?: string,
    infoConnectionCode?: string
}

@CustomElement({
    name: "login-component",
    style: require("./loginComponent.scss"),
    template: require("./loginComponent.html"),
    i18n: require("./loginComponent.i18n").I18n
})
export class LoginComponent<I18n extends I18nLoginPage = I18nLoginPage> extends WebComponent<I18n> {

    /** components */
    @TypedHtmlElements({ ensureLoadedClasses: [CjsInputTextEditor] })
    public readonly components!: {
        loginForm: HTMLDivElement,
        loginInput: HTMLInputElement,
        passwordInput: DigitCodeComponent,
        loginButton: HTMLButtonElement,
        codeButton: HTMLButtonElement,
        messageContainer: HTMLDivElement,
        messageTitle: HTMLDivElement,
        messageBody: HTMLDivElement,
        codeForm: HTMLDivElement,
        loginContainer: HTMLDivElement,
        infoConnection: HTMLDivElement,
        connection: HTMLDivElement,
        mailTitle: HTMLDivElement,
        passwordTitle: HTMLDivElement,
        cardContainer: HTMLDivElement
    };

    @Injected(LOG_SERVICE, { optional: true })
    protected readonly _log?: LogService;

    @Injected(APP_SERVICE, { optional: true })
    protected readonly _app?: AppService;

    @Injected(ERROR_MESSAGE_SERVICE)
    protected readonly _errorMessageService!: ErrorMessageService;

    protected _loginInProgress = false;

    constructor(protected _settings: LoginSettings) {
        super();
        this._errorMessageService.registerErrorMessages(ERROR_MESSAGES);
        if(_settings.infoConnectionDisplay === false) {
            this.components.infoConnection.hidden = true;
        }
    }

    /**
     * Disable login button if there is no login or password in the inputs and there is no login in progress
     */
    private _updateLoginButtonState(password: boolean = false): void {
        const canConnect = !!this.components.loginInput.value;
        const canLogin = !!this.components.passwordInput.getValue() && password;
        this.components.loginButton.disabled = !canConnect;
        this.components.codeButton.disabled = !canLogin || this._loginInProgress;
    }

    /**
     * Callback called when pushing loginButton.
     * @param s 
     * @returns 
     */
    private _clickCallback = (s: string): Promise<void> => {
        try {
            return this._settings.sendCallback!(s).then((res) => {
                if (res) {
                    this.components.loginForm.hidden = true;
                    this.components.infoConnection.innerHTML = this._settings.infoConnectionCode ? this._settings.infoConnectionCode : this.i18n.infoConnection.code;
                    this._clearMessage();
                    this.components.codeForm.hidden = false;
                    return Promise.resolve();
                } else {
                    return Promise.reject("sendCallback return a false value");
                }
            }).catch((err) => {
                this._log?.error(err);
            });
        } catch (err) {
            this._log?.error("sendCallback just throws before launching a promise");
            this._log?.error(err);
            return Promise.resolve();
        }
    };

    /** Init component display */
    public override init(): void {

        if (this._settings.login) {
            // init and show login form
            this.components.codeButton.onclick = () => this._login();
            this.components.loginButton.onclick = () => {
                return this._clickCallback(this.components.loginInput.value);
            };
            this.components.loginInput.placeholder = this.i18n.loginInput.placeholder;
            // this.components.passwordInput.placeholder = this.i18n.passwordInput.placeholder;
            this.components.loginButton.innerHTML = this.i18n.loginButton.innerHTML;
            this.components.codeButton.innerHTML = this.i18n.codeButton.innerHTML;

            // disable loginButton until login and password contain both a value
            const loginInputModificationCallback = (): void => this._updateLoginButtonState();
            this.components.loginInput.oninput = loginInputModificationCallback;
            // this.components.passwordInput.oninput = loginInputModificationCallback;

            // login on user pressing ENTER
            this.components.loginInput.addEventListener("keyup", (ev) => {
                if (ev.key === KeyboardInput.ENTER) {
                    if (!this.components.loginButton.disabled) {
                        return this._clickCallback(this.components.loginInput.value);
                    }
                    ev.preventDefault();
                    ev.stopImmediatePropagation();
                    return Promise.resolve();
                }
                return Promise.resolve();
            });

            this.components.passwordInput.options.setAndRefreshIfChanged({
                nbrDigits: this._settings.nbrDigitPassword,
                onComplete: (full: boolean) => {
                    this._updateLoginButtonState(full);
                }
            });

            // display login form
            this.components.loginForm.hidden = false;
            this.components.infoConnection.innerHTML = this._settings.infoConnectionMail ? this._settings.infoConnectionMail : this.i18n.infoConnection.mail;
            this.components.connection.innerHTML = this.i18n.connection;
            if(this._settings.noMailTitle) {
                // Do nothing
            } else {
                this.components.mailTitle.innerHTML = this.i18n.mailTitle;
            }
            if(this._settings.noPasswordTitle) {
                // Do nothing
            } else {
                this.components.passwordTitle.innerHTML = this.i18n.codeTitle;
            }

        }

        const searchParams = new URLSearchParams(window.location.search);
        const error = searchParams.get("error");
        if (error && Number(error) in AuthErrorCode) {
            this._showErrorFromErrorMessageService({ code: Number(error) });
        }

        this._updateLoginButtonState();
    }

    /**
     * Will call the login method with the form parameters
     * @returns promise of login method
     */
    private _login(): void {
        if (!this._settings.login) {
            return;
        }
        this._clearMessage();

        const login = this.components.loginInput.value;
        const password = this.components.passwordInput.getValue();

        if (password == null && this._app?.env !== Environment.DEV) {
            this._showErrorFromErrorMessageService({ code: AuthErrorCode.WRONG_LOGIN_PASSWORD });
            this._updateLoginButtonState();
            return;
        }
        this._loginInProgress = true;
        this._updateLoginButtonState();

        this._settings.login!(login, password!).then(() => {
            this._loginInProgress = false;
            this._updateLoginButtonState();
        }).catch(error => {
            this._loginInProgress = false;
            this._updateLoginButtonState();
            this._showErrorFromErrorMessageService(error);
        });
    }

    /** Clear message elements and reset message styling */
    public _clearMessage(): void {
        this.components.messageBody.innerText = "";
        this.components.messageTitle.innerText = "";
        this.components.messageContainer.classList.remove("visible", "info", "error");
    }

    /**
     * Display message with info styling
     * @param body body message. Will not be visible if isVisible isn't true
     * @param title Optional. Title to display with body
     * @param isVisible Optional. Set it to true if you want the body of your message to be visible 
     */
    public showInfo(body: string, title?: string, isVisible?: boolean): void {
        this.components.messageTitle.innerText = title != null && title.length > 0 ? title : "";
        this.components.messageBody.innerText = isVisible === true ? body : "";
        this.components.messageContainer.classList.remove("error");
        this.components.messageContainer.classList.add("info", "visible");
    }

    /**
     * Display message with error styling
     * @param error Message to display
     */
    public showError(error: string): void {
        this.components.messageBody.innerText = error;
        this.components.messageContainer.classList.remove("info");
        this.components.messageContainer.classList.add("error", "visible");
    }

    /**
     * Display error in message, using errorMessageService
     */
    private _showErrorFromErrorMessageService(error: ErrorWithCode): Promise<void> {
        return this._errorMessageService.getErrorMessage(error).then((errorMessage) => {
            if (errorMessage) {
                this.showError(errorMessage);
            }
        });
    }
}